import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { Attachment, ChatDetail, ChatMessage, ChatSummary, Source, StreamEvent, ToolActivity } from '@medicail/shared';
import { apiFetchRaw, errorFromResponse, errorMessage } from '@/lib/http';
import { readStreamEvents } from '@/lib/sse';
import { chatKeys } from './api';

export interface LiveAssistant {
  messageId: string;
  content: string;
  tools: ToolActivity[];
  sources: Source[];
}

/** Persisted message plus a stable React key that survives the tmp -> server id swap. */
export type CachedMessage = ChatMessage & { clientKey?: string };

export interface SendInput {
  content: string;
  attachments: Attachment[];
}

export interface ChatStreamState {
  phase: 'idle' | 'streaming' | 'error';
  live: LiveAssistant | null;
  error: { message: string; retryable: boolean } | null;
  lastRequest: SendInput | null;
}

const IDLE: ChatStreamState = { phase: 'idle', live: null, error: null, lastRequest: null };

function mergeSources(a: Source[], b: Source[] | undefined): Source[] {
  if (!b?.length) return a;
  const seen = new Set(a.map((s) => s.url));
  return [...a, ...b.filter((s) => !seen.has(s.url))];
}

/**
 * Holds in-flight assistant turns per chat, outside React, so a stream keeps running while the user
 * browses other chats. Persisted data lives in the TanStack Query cache; this store only owns the
 * transient "live" assistant message.
 */
export class StreamStore {
  private states = new Map<string, ChatStreamState>();
  private listeners = new Set<() => void>();
  private controllers = new Map<string, AbortController>();
  private pendingDelta = new Map<string, string>();
  private raf = new Map<string, number>();

  constructor(private qc: QueryClient) {}

  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  getState(chatId: string | undefined): ChatStreamState {
    return (chatId && this.states.get(chatId)) || IDLE;
  }

  isStreaming(chatId: string | undefined): boolean {
    return this.getState(chatId).phase === 'streaming';
  }

  private set(chatId: string, patch: Partial<ChatStreamState>) {
    this.states.set(chatId, { ...this.getState(chatId), ...patch });
    this.listeners.forEach((l) => l());
  }

  private updateLive(chatId: string, fn: (live: LiveAssistant) => LiveAssistant) {
    const live = this.getState(chatId).live;
    if (live) this.set(chatId, { live: fn(live) });
  }

  private flushDelta(chatId: string) {
    const raf = this.raf.get(chatId);
    if (raf !== undefined) cancelAnimationFrame(raf);
    this.raf.delete(chatId);
    const delta = this.pendingDelta.get(chatId);
    if (delta) {
      this.pendingDelta.delete(chatId);
      this.updateLive(chatId, (l) => ({ ...l, content: l.content + delta }));
    }
  }

  private queueDelta(chatId: string, delta: string) {
    this.pendingDelta.set(chatId, (this.pendingDelta.get(chatId) ?? '') + delta);
    if (!this.raf.has(chatId)) {
      this.raf.set(chatId, requestAnimationFrame(() => this.flushDelta(chatId)));
    }
  }

  abort(chatId: string) {
    this.controllers.get(chatId)?.abort();
  }

  abortAll() {
    this.controllers.forEach((c) => c.abort());
  }

  dismissError(chatId: string) {
    if (this.getState(chatId).phase === 'error') this.set(chatId, { ...IDLE });
  }

  retry(chatId: string) {
    const req = this.getState(chatId).lastRequest;
    if (req) void this.send(chatId, req);
  }

  private patchChat(chatId: string, fn: (chat: ChatDetail) => ChatDetail) {
    this.qc.setQueryData<ChatDetail>(chatKeys.detail(chatId), (old) => (old ? fn(old) : old));
  }

  async send(chatId: string, input: SendInput): Promise<void> {
    if (this.isStreaming(chatId)) return;
    const { qc } = this;

    // Optimistic user message. If the last message is already this user turn (a retry), reuse it.
    const tmpId = `tmp-${Date.now()}`;
    const tmp: ChatMessage = {
      messageId: tmpId,
      chatId,
      role: 'user',
      content: input.content,
      attachments: input.attachments.length ? input.attachments : undefined,
      createdAt: new Date().toISOString(),
    };
    this.patchChat(chatId, (chat) => {
      const last = chat.messages[chat.messages.length - 1];
      if (last?.role === 'user' && last.content === input.content) return chat;
      // Drop a failed assistant turn so the retry does not stack under it.
      const msgs = last?.role === 'assistant' && last.error ? chat.messages.slice(0, -1) : chat.messages;
      return { ...chat, messages: [...msgs, tmp] };
    });

    const controller = new AbortController();
    this.controllers.set(chatId, controller);
    this.set(chatId, {
      phase: 'streaming',
      live: { messageId: '', content: '', tools: [], sources: [] },
      error: null,
      lastRequest: input,
    });

    let finished = false;
    try {
      const res = await apiFetchRaw(`/chats/${chatId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ content: input.content, attachmentIds: input.attachments.map((a) => a.attachmentId) }),
        signal: controller.signal,
      });
      if (!res.ok) throw await errorFromResponse(res);
      if (!res.body) throw new Error('The server returned an empty response.');

      for await (const ev of readStreamEvents(res.body)) {
        if (this.handleEvent(chatId, ev, tmpId)) finished = true;
      }
      this.flushDelta(chatId);
      if (!finished) {
        this.set(chatId, {
          phase: 'error',
          error: { message: 'The connection was interrupted before the answer finished.', retryable: true },
        });
      }
    } catch (err) {
      this.flushDelta(chatId);
      if (controller.signal.aborted) {
        this.finishAborted(chatId);
      } else {
        this.set(chatId, { phase: 'error', error: { message: errorMessage(err), retryable: true } });
      }
    } finally {
      this.controllers.delete(chatId);
      void qc.invalidateQueries({ queryKey: chatKeys.list });
    }
  }

  /** User pressed Stop: keep whatever text arrived as a local assistant message. */
  private finishAborted(chatId: string) {
    const live = this.getState(chatId).live;
    if (live && (live.content || live.sources.length)) {
      const msg: ChatMessage = {
        messageId: live.messageId || `local-${Date.now()}`,
        chatId,
        role: 'assistant',
        content: live.content,
        sources: live.sources.length ? live.sources : undefined,
        createdAt: new Date().toISOString(),
      };
      this.patchChat(chatId, (chat) => ({ ...chat, messages: [...chat.messages.filter((m) => m.messageId !== msg.messageId), msg] }));
    }
    this.set(chatId, { ...IDLE, lastRequest: this.getState(chatId).lastRequest });
  }

  /** Returns true when the event ends the turn successfully. */
  private handleEvent(chatId: string, ev: StreamEvent, tmpId: string): boolean {
    switch (ev.type) {
      case 'user_message':
        this.patchChat(chatId, (chat) => {
          const idx = chat.messages.findIndex((m) => m.messageId === tmpId);
          const lastIdx = chat.messages.length - 1;
          const last = chat.messages[lastIdx];
          const replaceAt = idx >= 0 ? idx : last?.role === 'user' && last.content === ev.message.content ? lastIdx : -1;
          if (replaceAt >= 0) {
            const msgs = [...chat.messages];
            // Keep the client key so the bubble does not remount (and re-animate) when the server id arrives.
            const prev = msgs[replaceAt] as CachedMessage;
            msgs[replaceAt] = { ...ev.message, clientKey: prev.clientKey ?? prev.messageId } as CachedMessage;
            return { ...chat, messages: msgs };
          }
          return { ...chat, messages: [...chat.messages, ev.message] };
        });
        return false;
      case 'assistant_start':
        this.updateLive(chatId, (l) => ({ ...l, messageId: ev.messageId }));
        return false;
      case 'text_delta':
        this.queueDelta(chatId, ev.delta);
        return false;
      case 'tool_start':
        this.updateLive(chatId, (l) => ({ ...l, tools: [...l.tools.filter((t) => t.toolUseId !== ev.tool.toolUseId), ev.tool] }));
        return false;
      case 'tool_end':
        this.updateLive(chatId, (l) => ({
          ...l,
          tools: l.tools.map((t) => (t.toolUseId === ev.toolUseId ? { ...t, status: ev.status } : t)),
          sources: mergeSources(l.sources, ev.sources),
        }));
        return false;
      case 'chat_title':
        this.qc.setQueryData<ChatSummary[]>(chatKeys.list, (old) => old?.map((c) => (c.chatId === ev.chatId ? { ...c, title: ev.title } : c)));
        this.patchChat(ev.chatId, (chat) => ({ ...chat, title: ev.title }));
        return false;
      case 'assistant_done': {
        this.flushDelta(chatId);
        this.patchChat(chatId, (chat) => ({
          ...chat,
          updatedAt: ev.message.createdAt,
          messageCount: chat.messages.length + 1,
          messages: [...chat.messages.filter((m) => m.messageId !== ev.message.messageId), ev.message],
        }));
        this.set(chatId, { phase: ev.message.error ? 'error' : 'idle', live: null, error: ev.message.error ? { message: ev.message.error, retryable: true } : null });
        return true;
      }
      case 'error':
        this.flushDelta(chatId);
        this.set(chatId, { phase: 'error', error: { message: ev.message, retryable: ev.retryable } });
        return true;
      case 'ping':
      default:
        return false;
    }
  }
}

export const StreamStoreContext = createContext<StreamStore | null>(null);

export function useStreamStore(): StreamStore {
  const s = useContext(StreamStoreContext);
  if (!s) throw new Error('StreamStoreContext missing');
  return s;
}

export function useCreateStreamStore(qc: QueryClient): StreamStore {
  const [store] = useState(() => new StreamStore(qc));
  useEffect(() => () => store.abortAll(), [store]);
  return store;
}

export function useChatStream(chatId: string | undefined): ChatStreamState {
  const store = useStreamStore();
  return useSyncExternalStore(store.subscribe, () => store.getState(chatId), () => IDLE);
}
